import * as React from 'react';
import { createPortal } from 'react-dom';
import { motion, useMotionValue, useTransform, PanInfo, useDragControls, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { usePortalContainer } from '@/contexts/PhoneFrameContext';

interface DraggableBottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  title?: React.ReactNode;
  /** Custom z-index class for stacking above higher-z elements (e.g. lightbox) */
  zIndexClass?: string;
  className?: string;
  /** Height of the sheet - can be a percentage like "85vh" or fixed height */
  height?: string;
  /** Whether to show the close button in the header */
  showCloseButton?: boolean;
  /** Custom header content (replaces title and close button) */
  customHeader?: React.ReactNode;
  /** Decorative element that appears above the sheet (e.g., hero image) */
  heroElement?: React.ReactNode;
}

const CLOSE_THRESHOLD = 100; // pixels to drag before closing
const VELOCITY_THRESHOLD = 500; // velocity threshold for quick swipes

export const DraggableBottomSheet = React.forwardRef<
  HTMLDivElement,
  DraggableBottomSheetProps
>(function DraggableBottomSheet(
  {
    open,
    onOpenChange,
    children,
    title,
    className,
    height = '85vh',
    showCloseButton = true,
    customHeader,
    heroElement,
    zIndexClass,
  },
  ref
) {
  const y = useMotionValue(0);
  const portalContainer = usePortalContainer();
  const opacity = useTransform(y, [0, 300], [1, 0.5]);
  const scale = useTransform(y, [0, 300], [1, 0.95]);
  const dragControls = useDragControls();

  const handleClose = React.useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleDragEnd = React.useCallback((_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const shouldClose = 
      info.offset.y > CLOSE_THRESHOLD || 
      info.velocity.y > VELOCITY_THRESHOLD;
    
    if (shouldClose) {
      handleClose();
    }
  }, [handleClose]);

  const startDrag = (event: React.PointerEvent) => {
    event.stopPropagation();
    dragControls.start(event);
  };

  const handleBackdropClick = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    handleClose();
  }, [handleClose]);

  // Reset position when sheet opens
  React.useEffect(() => {
    if (open) {
      y.set(0);
    }
  }, [open, y]);

  const sheetContent = (
    <AnimatePresence mode="wait">
      {open && (
        <motion.div 
          ref={ref} 
          className={cn("fixed inset-0 z-[55]", zIndexClass)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/80"
            onClick={handleBackdropClick}
          />

          {/* Sheet Container - includes hero element and sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
              type: 'spring',
              damping: 30,
              stiffness: 300,
              mass: 0.8,
            }}
            style={{
              y,
              opacity,
              scale,
              // Allow vertical scrolling inside the sheet on iOS/Safari.
              // Framer Motion sets `touch-action: none` on draggable elements by default,
              // which can block inner overflow scrolling.
              touchAction: 'pan-y',
            }}
            drag="y"
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDragEnd={handleDragEnd}
            className="absolute inset-x-0 bottom-0 max-h-full"
          >
            <div className="relative">
              {/* Hero Element - positioned above sheet, pulled down to overlap, sits behind sheet content */}
              {heroElement && (
                <div 
                  className="absolute left-1/2 -translate-x-1/2 bottom-full pointer-events-none"
                  style={{ marginBottom: '-9rem', transform: 'translateX(-50%) translateY(5px)' }}
                >
                  {heroElement}
                </div>
              )}

              {/* Sheet - z-10 to appear on top of the overlapping hero portion */}
              <div
                className={cn(
                  'relative z-10 flex flex-col rounded-t-3xl bg-background shadow-lg max-h-[90vh]',
                  height === 'auto' ? 'h-auto' : '',
                  className
                )}
                style={{
                  height: height !== 'auto' ? height : undefined,
                }}
              >
              {/* Drag Handle - Only this area triggers swipe-to-close */}
              <div
                className="flex justify-center py-3 cursor-grab active:cursor-grabbing touch-none select-none"
                onPointerDown={startDrag}
              >
                <div className="w-10 h-1 bg-muted-foreground/30 rounded-full" />
              </div>

              {/* Header */}
              {customHeader ? (
                customHeader
              ) : title || showCloseButton ? (
                <div className="flex items-center justify-between px-5 pb-3">
                  <div className="flex-1">{typeof title === 'string' ? <h2 className="text-lg font-semibold">{title}</h2> : title}</div>
                  {showCloseButton && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleClose}
                      className="h-8 w-8 shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ) : (
                /* Always show X button in drag handle area when no header */
                <div className="flex justify-end px-5 -mt-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleClose}
                    className="h-8 w-8"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}

              {/* Content */}
              <div
                className={cn(
                  'flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y',
                  height === 'auto' ? 'pb-4' : ''
                )}
                style={{
                  WebkitOverflowScrolling: 'touch',
                  touchAction: 'pan-y',
                }}
              >
                {children}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
      )}
    </AnimatePresence>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(sheetContent, portalContainer);
});

// Hook for using draggable sheet with AnimatePresence
export function useDraggableSheet(initialOpen = false) {
  const [isOpen, setIsOpen] = React.useState(initialOpen);
  
  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen(prev => !prev),
    onOpenChange: setIsOpen,
  };
}
